/**
 * useApproveToken — handles two-step Permit2 approval flow.
 *
 * Step 1: ERC20.approve(PERMIT2_ADDRESS, MAX_UINT256) — one-time per token
 * Step 2: Permit2.approve(token, UNIVERSAL_ROUTER, MAX_UINT160, expiration) — 30-day TTL
 *
 * Uses useTransactionState() for status tracking.
 *
 * ARCH-25: Transaction lifecycle state machine
 * ARCH-27: Contract interaction hook pattern
 * Story 5.2 Task 3
 */

import { useCallback, useMemo } from "react";
import { maxUint256 } from "viem";
import { getPublicClient, getWalletClient } from "wagmi/actions";

import { erc20Abi } from "../../lib/abis/erc20";
import { permit2Abi } from "../../lib/abis/permit2";
import { getChainAddresses } from "../../lib/chain-addresses";
import { isUserRejection, mapChainError } from "../../lib/chain-errors";
import { getActiveChain, wagmiConfig } from "../../lib/wagmi-config";
import { useTransactionState } from "./use-transaction-state";
import type { TransactionState } from "./use-transaction-state";

/** Max uint160 for Permit2 amount */
const MAX_UINT160 = BigInt("0xFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFF");

/** 30 days in seconds */
const THIRTY_DAYS_SECONDS = 30 * 24 * 60 * 60;

type UseApproveTokenReturn = {
	approveErc20: () => Promise<void>;
	approvePermit2: () => Promise<void>;
	txState: TransactionState;
	reset: () => void;
};

/**
 * Hook to handle the two-step Permit2 approval flow.
 *
 * @param tokenAddress - The ERC-20 token contract address to approve
 * @returns Approval functions and transaction state
 */
export function useApproveToken(tokenAddress: `0x${string}`): UseApproveTokenReturn {
	const {
		state: txState,
		setPreparing,
		setAwaitingSignature,
		setPending,
		setConfirmed,
		setFailed,
		setRejected,
		reset,
	} = useTransactionState();

	const approveErc20 = useCallback(async () => {
		try {
			reset(); // Clear any stale state from a previous approval step
			setPreparing();

			const activeChain = getActiveChain();
			const addresses = getChainAddresses(activeChain.id);
			if (!addresses) {
				setFailed(`Unsupported chain: ${activeChain.id}`);
				return;
			}

			const walletClient = await getWalletClient(wagmiConfig, { chainId: activeChain.id });
			const publicClient = getPublicClient(wagmiConfig, { chainId: activeChain.id });

			if (!publicClient) {
				setFailed("Network connection unavailable.");
				return;
			}

			setAwaitingSignature();

			// maxUint256 is intentional: ERC-20 → Permit2 is a one-time blanket approval.
			// Permit2 provides fine-grained amount + expiration controls (step 2).
			const txHash = await walletClient.writeContract({
				address: tokenAddress,
				abi: erc20Abi,
				functionName: "approve",
				args: [addresses.permit2, maxUint256],
			});

			setPending(txHash);

			const receipt = await publicClient.waitForTransactionReceipt({ hash: txHash });
			if (receipt.status === "reverted") {
				setFailed("Approval transaction reverted on-chain.");
				return;
			}

			setConfirmed();
		} catch (error: unknown) {
			if (isUserRejection(error)) {
				setRejected();
			} else {
				setFailed(mapChainError(error));
			}
		}
	}, [
		tokenAddress,
		reset,
		setPreparing,
		setAwaitingSignature,
		setPending,
		setConfirmed,
		setFailed,
		setRejected,
	]);

	const approvePermit2 = useCallback(async () => {
		try {
			reset(); // Clear stale state from ERC-20 approval step
			setPreparing();

			const activeChain = getActiveChain();
			const addresses = getChainAddresses(activeChain.id);
			if (!addresses) {
				setFailed(`Unsupported chain: ${activeChain.id}`);
				return;
			}

			const walletClient = await getWalletClient(wagmiConfig, { chainId: activeChain.id });
			const publicClient = getPublicClient(wagmiConfig, { chainId: activeChain.id });

			if (!publicClient) {
				setFailed("Network connection unavailable.");
				return;
			}

			setAwaitingSignature();

			const expiration = Math.floor(Date.now() / 1000) + THIRTY_DAYS_SECONDS;

			const txHash = await walletClient.writeContract({
				address: addresses.permit2,
				abi: permit2Abi,
				functionName: "approve",
				args: [tokenAddress, addresses.universalRouter, MAX_UINT160, expiration],
			});

			setPending(txHash);

			const receipt = await publicClient.waitForTransactionReceipt({ hash: txHash });
			if (receipt.status === "reverted") {
				setFailed("Permit2 approval transaction reverted on-chain.");
				return;
			}

			setConfirmed();
		} catch (error: unknown) {
			if (isUserRejection(error)) {
				setRejected();
			} else {
				setFailed(mapChainError(error));
			}
		}
	}, [
		tokenAddress,
		reset,
		setPreparing,
		setAwaitingSignature,
		setPending,
		setConfirmed,
		setFailed,
		setRejected,
	]);

	return useMemo(
		() => ({ approveErc20, approvePermit2, txState, reset }),
		[approveErc20, approvePermit2, txState, reset]
	);
}
